import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PawLinkLogo } from "@/components/PawLinkLogo";
import { ArrowRight, Lock, CheckCircle } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import api from "@/lib/api";

export default function ResetPassword() {
    const { t } = useI18n();
    const { hydrateFromReset } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const token = useMemo(() => params.get("token") || "", [params]);

    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setError(t("auth.reset_no_token"));
    }, [token, t]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!token) { setError(t("auth.reset_no_token")); return; }
        if (pwd.length < 6) { setError(t("auth.password_hint")); return; }
        if (pwd !== pwd2)  { setError(t("auth.reset_mismatch")); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, new_password: pwd });
            if (data?.token && typeof hydrateFromReset === "function") {
                hydrateFromReset(data);
            }
            setDone(true);
            setTimeout(() => nav("/home"), 1500);
        } catch (err) {
            const msg = err?.response?.data?.detail;
            setError(msg || t("auth.reset_invalid_link"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-paw-night">
            <Nav />
            <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-24 -start-24 w-[420px] h-[420px] rounded-full bg-paw-terra/10 dark:bg-paw-terra/[0.06] blur-[120px]" />
                <div className="absolute bottom-0 -end-32 w-[520px] h-[520px] rounded-full bg-paw-terra/8 dark:bg-paw-terra/[0.04] blur-[140px]" />
            </div>

            <div className="relative pt-24 sm:pt-28 pb-16 px-5 sm:px-6">
                <div className="w-full max-w-[400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-10">
                        <Link to="/" className="inline-block mb-6 transition-transform hover:scale-[1.03]">
                            <PawLinkLogo variant="stacked" size={64} />
                        </Link>
                        <h1 className="font-display text-[32px] sm:text-[38px] leading-[1.1] font-medium tracking-tight text-paw-ink dark:text-white">
                            {t("auth.reset_title")}
                        </h1>
                        <p className="mt-2.5 text-[15px] text-paw-ink2 dark:text-paw-silver">
                            {t("auth.reset_sub")}
                        </p>
                    </div>

                    <div className="card-soft rounded-3xl p-6 sm:p-8 space-y-5 shadow-hover" data-testid="reset-password-form-wrap">
                        {done ? (
                            <div className="flex flex-col items-center text-center py-6" data-testid="reset-password-success">
                                <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 grid place-items-center">
                                    <CheckCircle size={26} weight="fill" className="text-emerald-500" />
                                </div>
                                <p className="mt-4 text-sm text-paw-ink2 dark:text-paw-bone/70 leading-relaxed">
                                    {t("auth.reset_success")}
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                                <div>
                                    <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                        <Lock size={12} weight="duotone" /> {t("auth.reset_new")}
                                    </label>
                                    <input
                                        data-testid="reset-password-new"
                                        type="password" required minLength={6} autoComplete="new-password"
                                        className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                        value={pwd} onChange={(e) => setPwd(e.target.value)}
                                        placeholder="••••••••"
                                    />
                                    <p className="mt-1.5 text-[11px] text-paw-ink2/70 dark:text-paw-bone/50">
                                        {t("auth.password_hint")}
                                    </p>
                                </div>
                                <div>
                                    <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                        <Lock size={12} weight="duotone" /> {t("auth.reset_confirm")}
                                    </label>
                                    <input
                                        data-testid="reset-password-confirm"
                                        type="password" required minLength={6} autoComplete="new-password"
                                        className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                        value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                                        placeholder="••••••••"
                                    />
                                </div>

                                {error && (
                                    <div data-testid="reset-password-error"
                                        className="rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit" disabled={loading || !token}
                                    data-testid="reset-password-submit"
                                    className="btn-primary w-full h-12 rounded-2xl text-[15px] tracking-tight disabled:opacity-60"
                                >
                                    {loading ? t("auth.reset_saving") : t("auth.reset_submit")}
                                    <ArrowRight size={16} className="ms-1.5" />
                                </button>
                            </form>
                        )}
                    </div>

                    <p className="text-center text-sm text-paw-ink2 dark:text-paw-bone/60 mt-6">
                        <Link to="/login" data-testid="reset-password-back"
                            className="text-paw-forest dark:text-paw-terra font-semibold hover:underline underline-offset-4">
                            {t("auth.forgot_back_login")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
